import {
  type PolymorphicComponent,
  type PolymorphicProps,
  type SlotComponentProps,
  useSlotProps,
} from '@mui/base'
import { type ElementType, type ForwardedRef, forwardRef } from 'react'

type ProductBadgeOwnProps = {
  slotProps?: {
    root?: SlotComponentProps<'span', undefined, undefined>
  }

  slots?: {
    root?: ElementType
  }
}

interface ProductBadgeTypeMap<
  AdditionalProps = NonNullable<unknown>,
  RootComponentType extends ElementType = 'span',
> {
  defaultComponent: RootComponentType
  props: AdditionalProps & ProductBadgeOwnProps
}

type ProductBadgeProps<
  RootComponentType extends
    ElementType = ProductBadgeTypeMap['defaultComponent'],
> = PolymorphicProps<
  ProductBadgeTypeMap<NonNullable<unknown>, RootComponentType>,
  RootComponentType
>

export const ProductBadge = forwardRef(function ProductBadge(
  props: ProductBadgeProps,
  ref: ForwardedRef<HTMLSpanElement>,
) {
  const { slotProps = {}, slots = {}, ...other } = props

  const Root = slots.root ?? 'span'
  const rootProps = useSlotProps({
    additionalProps: {
      ref,
    },
    className: 'product-badge',
    elementType: Root,
    externalForwardedProps: other,
    externalSlotProps: slotProps.root,
    ownerState: undefined,
  })

  return <Root {...rootProps} />
}) as PolymorphicComponent<ProductBadgeTypeMap>
